import React from "react";
import {
    Box,
    Container,
    Paper,
    Stack,
    TableCell,
    TableRow,
    Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

/** Shared layout for all admin finance screens */
export function FinancePageContainer({ children, maxWidth = "xl", ...props }) {
    return (
        <Container
            maxWidth={maxWidth}
            disableGutters={false}
            sx={{
                py: { xs: 2, md: 3 },
                pb: { xs: 4, md: 6 },
                px: { xs: 1.5, sm: 2 },
            }}
            {...props}
        >
            {children}
        </Container>
    );
}

/**
 * Top hero band: title, optional subtitle, optional right-side actions.
 * @param {string} [eyebrow] — small label above title (default "Finance")
 */
export function FinancePageHeader({ eyebrow = "Finance", title, subtitle, action }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 2.5, md: 3 },
                mb: 3,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                background: (theme) =>
                    `linear-gradient(125deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(
                        theme.palette.primary.dark,
                        0.04
                    )} 45%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            }}
        >
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "flex-start" }}
                justifyContent="space-between"
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="overline"
                        sx={{
                            color: "primary.main",
                            fontWeight: 800,
                            letterSpacing: "0.14em",
                            lineHeight: 1.2,
                        }}
                    >
                        {eyebrow}
                    </Typography>
                    <Typography
                        variant="h4"
                        component="h1"
                        fontWeight={800}
                        sx={{
                            fontSize: { xs: "1.35rem", sm: "1.65rem", md: "1.85rem" },
                            mt: 0.25,
                            lineHeight: 1.25,
                            letterSpacing: "-0.02em",
                        }}
                    >
                        {title}
                    </Typography>
                    {subtitle ? (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1, maxWidth: 720, lineHeight: 1.6 }}
                        >
                            {subtitle}
                        </Typography>
                    ) : null}
                </Box>
                {action ? (
                    <Box
                        sx={{
                            flexShrink: 0,
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 1,
                            alignItems: { sm: "center" },
                        }}
                    >
                        {action}
                    </Box>
                ) : null}
            </Stack>
        </Paper>
    );
}

/** Outlined panel with optional title bar */
export function FinancePanel({ title, description, action, children, sx = {}, contentSx = {}, className }) {
    const hasBar = Boolean(title || description || action);
    return (
        <Paper
            className={className}
            variant="outlined"
            elevation={0}
            sx={{
                borderRadius: 2,
                overflow: "hidden",
                borderColor: "divider",
                boxShadow: (theme) => theme.shadows[1],
                ...sx,
            }}
        >
            {hasBar ? (
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                    >
                        <Box>
                            {title ? (
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {title}
                                </Typography>
                            ) : null}
                            {description ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {description}
                                </Typography>
                            ) : null}
                        </Box>
                        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
                    </Stack>
                </Box>
            ) : null}
            <Box sx={{ p: hasBar ? 2.5 : 0, ...contentSx }}>{children}</Box>
        </Paper>
    );
}

export const financeTableContainerSx = {
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid",
    borderColor: "divider",
    boxShadow: (theme) => theme.shadows[1],
};

export const FinanceHeadCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
    whiteSpace: "nowrap",
}));

export const FinanceTableRow = styled(TableRow)(({ theme }) => ({
    transition: theme.transitions.create("background-color", { duration: theme.transitions.duration.shorter }),
    "&:nth-of-type(even)": {
        backgroundColor: alpha(theme.palette.common.black, 0.02),
    },
    "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
    "&:last-of-type td": {
        borderBottom: 0,
    },
}));

/** Compact KPI tile used in collection / defaulters */
export function FinanceStatTile({ label, value, valueColor = "text.primary", helper }) {
    return (
        <Paper
            variant="outlined"
            elevation={0}
            sx={{
                p: 2,
                height: "100%",
                borderRadius: 2,
                borderColor: "divider",
                boxShadow: (t) => t.shadows[1],
                background: (theme) => alpha(theme.palette.background.paper, 0.9),
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: "0.04em" }}>
                {label}
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 800, color: valueColor }}>
                {value}
            </Typography>
            {helper ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {helper}
                </Typography>
            ) : null}
        </Paper>
    );
}
